import Vue from 'vue';
import types from '../mutation-types';
import authAPI from '../../api/auth';

import {
  setUser,
  clearCookiesOnLogout,
  clearLocalStorageOnLogout,
} from '../utils/api';

const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
  },
  uiFlags: {
    isFetching: true,
  },
};

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.availability;
  },

  getCurrentUserAutoOffline($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount.auto_offline;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );

    if (!currentAccount) {
      const associateAccount = accounts.find(account =>
        account.associate_accounts.includes($getters.getCurrentAccountId)
      );

      return associateAccount?.role;
    }
    return currentAccount?.role;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  getMessageSignature($state) {
    const { message_signature: messageSignature } = $state.currentUser;

    return messageSignature || '';
  },

  getCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentAccount || {};
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    const newSort = accounts?.sort((first, second) => {
      if (first?.name.toUpperCase() > second?.name.toUpperCase()) return 1;
      if (first?.name.toUpperCase() < second?.name.toUpperCase()) return -1;
      return 0;
    });

    return newSort;
  },
};

// actions
// Sequência das trocas de disponibilidade: garante que respostas fora de ordem
// (trocas consecutivas rápidas) não sobrescrevam o status com um valor antigo.
let availabilityRequestSeq = 0;

export const actions = {
  login(_, { ssoAccountId, ssoConversationId, ...credentials }) {
    return new Promise((resolve, reject) => {
      authAPI
        .login(credentials)
        .then(response => {
          clearLocalStorageOnLogout();
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  async verifyCode2F(_, payload) {
    const { email } = payload;
    if (!email) {
      const response = await authAPI.verifyCode2F(payload);
      return response.validated;
    }
    const response = await authAPI.verifyCodeEmail(payload);
    return response.validated;
  },

  async generateCode(_, payload) {
    const response = await authAPI.generateCode(payload);
    return response;
  },

  async sendCodeEmail(_, payload) {
    const response = await authAPI.sendCodeEmail(payload);
    return response;
  },

  async validityCheck(context) {
    try {
      const response = await authAPI.validityCheck();
      const currentUser = response.data.payload.data;
      setUser(currentUser);
      context.commit(types.SET_CURRENT_USER, currentUser);
    } catch (error) {
      if (error?.response?.status === 401) {
        clearCookiesOnLogout();
      }
    }
  },
  async setUser({ commit, dispatch }) {
    if (authAPI.hasAuthCookie()) {
      await dispatch('validityCheck');
    } else {
      commit(types.CLEAR_USER);
    }
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
  },
  logout({ commit }) {
    commit(types.CLEAR_USER);
  },

  updateProfile: async ({ commit }, params) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await authAPI.profileUpdate(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      throw error;
    }
  },

  deleteAvatar: async () => {
    try {
      await authAPI.deleteAvatar();
    } catch (error) {
      // Ignore error
    }
  },

  updateUISettings: async ({ commit }, params) => {
    try {
      commit(types.SET_CURRENT_USER_UI_SETTINGS, params);
      const response = await authAPI.updateUISettings(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  updateAvailability: async ({ commit, dispatch, state }, params) => {
    const seq = (availabilityRequestSeq += 1);
    // Atualização OTIMISTA: a pílula da topbar e a miniatura mudam na hora,
    // sem esperar a resposta da API (que é o que causava a lentidão).
    commit(types.SET_CURRENT_USER_AVAILABILITY, params.availability);
    dispatch('agents/updateSingleAgentPresence', {
      id: state.currentUser.id,
      availabilityStatus: params.availability,
    });
    try {
      const response = await authAPI.updateAvailability(params);
      // Ignora respostas fora de ordem: em trocas consecutivas, só a última
      // requisição pode reescrever o usuário (evita voltar a um status antigo).
      if (seq === availabilityRequestSeq) {
        commit(types.SET_CURRENT_USER, response.data);
      }
    } catch (error) {
      // Mantém o estado otimista; um refetch/websocket corrige se necessário.
    }
  },

  updateAutoOffline: async ({ commit, state }, { accountId, autoOffline }) => {
    // Atualização OTIMISTA: o toggle reflete na hora, sem esperar a API.
    const accounts = (state.currentUser.accounts || []).map(acc =>
      acc.id === accountId ? { ...acc, auto_offline: autoOffline } : acc
    );
    commit(types.SET_CURRENT_USER, { ...state.currentUser, accounts });
    try {
      const response = await authAPI.updateAutoOffline(accountId, autoOffline);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Mantém o estado otimista; um refetch corrige se necessário.
    }
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, data[$state.currentUser.id]);
    }
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      accounts,
    });
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = initialState.currentUser;
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    Vue.set(_state, 'currentUser', currentUser);
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    });
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, { isFetching }) {
    Vue.set(_state, 'uiFlags', { isFetching });
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
